/**
 * Console entry point — Open Sky.
 *
 * Provider order matters and is not arbitrary:
 *   ErrorBoundary   — outermost, so even a provider blowing up shows a real message.
 *   QueryClient     — the data layer.
 *   ToastProvider   — above the router, so a toast raised during navigation survives it.
 *   BrowserRouter   — deep links work because src/server.ts serves index.html for any
 *                     non-API GET that is not a real file.
 *
 * TWO STYLESHEETS during the transplant: base.css styles the reused real components (Login,
 * ErrorState, the toast host); opensky/styles.css carries the Open Sky design. They do not
 * collide — base.css uses its own class names — and opensky loads last. base.css goes when the
 * last real component the transition borrows is replaced.
 *
 * VITE_DEMO=1 mounts the Open Sky UI directly on its in-memory demo adapter, no session gate —
 * for local visual work where the real API (on the droplet, same-origin only) is unreachable.
 * The default (real) path gates on the live /api/me session and wires screens to the typed API.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import './styles/base.css';
import './opensky/styles.css';
import { createQueryClient } from './lib/useApi.ts';
import { ErrorBoundary, ToastProvider } from './components/index.ts';
import { App } from './opensky/App.tsx';
import { DemoProvider } from './opensky/lib/demo.tsx';
import { Root } from './opensky/Root.tsx';

const container = document.getElementById('root');
if (!container) {
  // Cannot happen with our index.html, but a blank page with no explanation is the one
  // failure mode this file must not produce.
  throw new Error('NightDesk console: #root is missing from the document.');
}

const demoOnly = import.meta.env.VITE_DEMO === '1';
const queryClient = createQueryClient();

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            {demoOnly ? (
              <DemoProvider>
                <App />
              </DemoProvider>
            ) : (
              <Root />
            )}
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
